import { Alert, Button, Empty, Form, Input, Modal, Skeleton, Space, Tag, Typography } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { staffApi } from './api';

export function BeadRail({ compact = false }: { compact?: boolean }) {
  const colors = ['#C9432F', '#E8A5B3', '#F6D86B', '#167A64', '#65AFC0', '#6457D9'];
  return (
    <div className={compact ? 'bead-rail bead-rail--compact' : 'bead-rail'} aria-hidden="true">
      {Array.from({ length: compact ? 10 : 18 }, (_, index) => (
        <span key={index} style={{ background: colors[index % colors.length] }} />
      ))}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-heading">
      <div>
        <Typography.Text className="page-heading__eyebrow">{eyebrow}</Typography.Text>
        <Typography.Title level={2}>{title}</Typography.Title>
        <Typography.Paragraph>{description}</Typography.Paragraph>
      </div>
      {actions ? <div className="page-heading__actions">{actions}</div> : null}
      <BeadRail compact />
    </header>
  );
}

export function MetricCard({
  label,
  value,
  note,
  tone = 'brick',
}: {
  label: string;
  value: ReactNode;
  note: string;
  tone?: 'brick' | 'purple' | 'green' | 'gold';
}) {
  return (
    <article className={`metric-card metric-card--${tone}`}>
      <span className="metric-card__label">{label}</span>
      <strong>{value}</strong>
      <span className="metric-card__note">{note}</span>
    </article>
  );
}

export function ResourceBoundary({
  loading,
  error,
  empty,
  onRetry,
  children,
}: {
  loading: boolean;
  error: string | null;
  empty: boolean;
  onRetry: () => void;
  children: ReactNode;
}) {
  if (loading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="数据没有加载成功"
        description={error}
        action={<Button onClick={onRetry}>重试</Button>}
      />
    );
  }
  if (empty) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无符合条件的数据" />;
  return children;
}

const statusMap: Record<string, { color: string; label: string }> = {
  ACTIVE: { color: 'green', label: '启用' },
  DISABLED: { color: 'default', label: '停用' },
  DRAFT: { color: 'default', label: '草稿' },
  TESTED: { color: 'blue', label: '测试通过' },
  PUBLISHED: { color: 'green', label: '已发布' },
  PREVIEW_READY: { color: 'cyan', label: '可预览' },
  PROCESSING: { color: 'processing', label: '处理中' },
  QUEUED: { color: 'processing', label: '排队中' },
  RUNNING: { color: 'processing', label: '运行中' },
  SUCCEEDED: { color: 'green', label: '成功' },
  FAILED: { color: 'red', label: '失败' },
  PENDING_PAYMENT: { color: 'gold', label: '待支付' },
  PAID: { color: 'blue', label: '待配货' },
  PACKING: { color: 'purple', label: '配货中' },
  SHIPPED: { color: 'cyan', label: '已发货' },
  COMPLETED: { color: 'green', label: '已完成' },
  CANCELLED: { color: 'default', label: '已取消' },
  REFUNDED: { color: 'volcano', label: '已退款' },
  OPEN: { color: 'gold', label: '待处理' },
  RESOLVED: { color: 'green', label: '已解决' },
};

export function StatusTag({ status }: { status: string }) {
  const config = statusMap[status] ?? { color: 'default', label: status };
  return <Tag color={config.color}>{config.label}</Tag>;
}

export function formatMoney(fen: number): string {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(fen / 100);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

interface ReauthValues {
  reason: string;
  password: string;
  totp?: string;
}

export function ReauthModal({
  open,
  title,
  description,
  onCancel,
  onVerified,
}: {
  open: boolean;
  title: string;
  description: string;
  onCancel: () => void;
  onVerified: (reauthToken: string, reason: string) => Promise<void>;
}) {
  const [form] = Form.useForm<ReauthValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    setError(null);
    try {
      const response = await staffApi.reauthenticate(values.password, values.totp);
      await onVerified(response.reauthToken, values.reason);
      form.resetFields();
      onCancel();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '验证失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      okText="验证并继续"
      cancelText="取消"
      confirmLoading={submitting}
      onCancel={onCancel}
      onOk={() => {
        void submit();
      }}
      destroyOnHidden
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert type="warning" showIcon message={description} />
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label="操作原因"
            name="reason"
            rules={[{ required: true, min: 5, message: '请填写至少 5 个字的操作原因' }]}
          >
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
          <Form.Item label="当前密码" name="password" rules={[{ required: true, min: 8 }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item label="TOTP（如已启用）" name="totp">
            <Input inputMode="numeric" maxLength={6} autoComplete="one-time-code" />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
}
